/**
 * Needs attention, `/needs-attention` (v1 completion I §10, the mock-up's
 * states 1–6): the conditions on orders, their lines and purchase orders that a
 * person should act on. Tabs Mine · My departments · Past due (each counted) ·
 * All; filters by kind, a search, and "Show acknowledged" (on: acknowledged
 * rows show muted). A row opens the alert in a side sheet.
 *
 * Filter, search, sort and paging run on the server (`listNeedsAttention`,
 * docs/list-endpoints.md); the page holds them in the URL, so a queue or an
 * open alert is a link (`?queue=past_due&alert=…`). Changing any of them goes
 * back to page 1.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse } from "@shared-core/data/adapter";
import type {
  AlertCommand,
  AlertKind,
  NeedsAttentionItem,
  NeedsAttentionQueue,
  NeedsAttentionRecord,
} from "@shared-core/data/needs-attention-types";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import {
  ALERT_KIND_LABELS,
  EMPTY_QUEUE,
  NEEDS_ATTENTION_QUEUES,
  isNeedsAttentionQueue,
  lastSweepWords,
  queueListOptions,
} from "@shared-core/domain/needs-attention";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { AlertSheet } from "../app/components/domain/needs-attention/AlertSheet";
import {
  DuePill,
  PriorityDot,
} from "../app/components/domain/needs-attention/AlertDue";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { SlideOverPanel } from "../app/components/patterns/SlideOverPanel";
import { Badge } from "../app/components/primitives/badge";
import { Checkbox } from "../app/components/primitives/checkbox";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { cn } from "../app/components/primitives/utils";
import { useCommandHost } from "../app/useCommandHost";

const PAGE_SIZE = 50;
const COUNTED: NeedsAttentionQueue[] = ["mine", "my_departments", "past_due"];
const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

function readFilters(params: URLSearchParams) {
  const queue = params.get("queue");
  const kind = params.get("kind");
  return {
    queue: isNeedsAttentionQueue(queue) ? queue : ("mine" as const),
    kind: kind && kind in ALERT_KIND_LABELS ? (kind as AlertKind) : undefined,
    q: params.get("q") || undefined,
    showAcknowledged: params.get("acknowledged") !== "hide",
    page: Math.max(1, Number(params.get("page")) || 1),
    alertId: params.get("alert"),
  };
}

type ListState = {
  /** The last page that arrived: kept on screen while the next loads. */
  list: ListResponse<NeedsAttentionItem> | null;
  isLoading: boolean;
  error: string | null;
};

export function NeedsAttentionPage() {
  const adapter = useDataAdapter();
  const index = useObjectManifest();
  const { openCommand, shell } = useCommandHost();
  const [params, setParams] = useSearchParams();
  const { queue, kind, q, showAcknowledged, page, alertId } =
    readFilters(params);
  const isFiltered = Boolean(kind || q || !showAcknowledged);

  const setParam = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page" && key !== "alert") next.delete("page");
        return next;
      },
      { replace: key !== "alert" },
    );

  const [query, setQuery] = useState(q ?? "");
  const debouncedQuery = useDebouncedValue(query, 300).trim();
  useEffect(() => {
    if (debouncedQuery !== (q ?? "")) setParam("q", debouncedQuery || null);
    // The URL's own q is the other direction; only a settled box writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const [listState, setListState] = useState<ListState>({
    list: null,
    isLoading: true,
    error: null,
  });
  const [counts, setCounts] = useState<
    Partial<Record<NeedsAttentionQueue, number>>
  >({});
  const [lastSweep, setLastSweep] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setListState((s) => ({ ...s, isLoading: true, error: null }));
    adapter
      .listNeedsAttention(
        queueListOptions(queue, {
          kind,
          q,
          state: showAcknowledged ? undefined : ["open"],
          page,
          pageSize: PAGE_SIZE,
        }),
      )
      .then((list) => {
        if (!cancelled) setListState({ list, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setListState((s) => ({
            ...s,
            isLoading: false,
            error:
              err instanceof Error ? err.message : "The alerts did not load.",
          }));
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, queue, kind, q, showAcknowledged, page, reloads]);

  // When the list was last brought up to date, and the tabs' counts: each queue's total, whatever the filters.
  useEffect(() => {
    let cancelled = false;
    adapter
      .getNeedsAttentionLastSweep()
      .then((sweep) => {
        if (!cancelled) setLastSweep(lastSweepWords(sweep.last_complete_at));
      })
      .catch(() => {
        // The header then says only how often it updates.
      });
    Promise.all(
      COUNTED.map((id) =>
        adapter
          .listNeedsAttention(queueListOptions(id, { pageSize: 1 }))
          .then((list) => [id, list.total] as const),
      ),
    )
      .then((found) => {
        if (!cancelled) setCounts(Object.fromEntries(found));
      })
      .catch(() => {
        // The list says when the server is down; a missing count shows none.
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, reloads]);

  const runSubjectCommand = (
    command: AlertCommand,
    record: NeedsAttentionRecord,
  ) => {
    openCommand({
      endpoint: {
        command: index.commandOf(command.name),
        href: command.href,
        dryRunHref: command.dry_run_href,
      },
      context: `${record.headline} · ${record.subject_ref}`,
    });
  };

  const { list, isLoading, error } = listState;
  const openAlert = alertId
    ? list?.items.find((i) => i.id === alertId)
    : undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="text-2xl font-medium">Needs attention</h1>
        <span className="text-xs text-muted-foreground">
          Updated every 10 minutes{lastSweep && ` · ${lastSweep}`}
        </span>
      </div>

      <nav
        aria-label="Queues"
        className="flex gap-5 overflow-x-auto border-b border-border"
      >
        {NEEDS_ATTENTION_QUEUES.map((tab) => {
          const isOn = tab.id === queue;
          const count = counts[tab.id];
          return (
            <button
              key={tab.id}
              type="button"
              aria-current={isOn ? "page" : undefined}
              onClick={() =>
                setParam("queue", tab.id === "mine" ? null : tab.id)
              }
              className={cn(
                "whitespace-nowrap py-2 text-sm",
                isOn
                  ? "border-b-2 border-primary text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {tab.label}
              {count !== undefined && (
                <span
                  className="ml-1 rounded-full bg-muted px-2 text-[11px]"
                  aria-label={`${count} alerts`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Kind"
          className={selectClass}
          value={kind ?? ""}
          onChange={(e) => setParam("kind", e.target.value || null)}
        >
          <option value="">Every kind</option>
          {Object.entries(ALERT_KIND_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <Input
          type="search"
          aria-label="Search"
          placeholder="Order, PO or customer"
          className="w-56"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={showAcknowledged}
            onCheckedChange={(checked) =>
              setParam("acknowledged", checked ? null : "hide")
            }
          />
          Show acknowledged
        </label>
      </div>

      {error ? (
        <ErrorPanel
          message={`The alerts did not load: ${error}`}
          onRetry={reload}
        />
      ) : list === null ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : list.total === 0 ? (
        <div className="rounded-lg border border-border px-4 py-10 text-center">
          {isFiltered ? (
            <p className="text-base">Nothing matches these filters.</p>
          ) : (
            <>
              <p className="text-base">{EMPTY_QUEUE}</p>
              <p className="text-xs text-muted-foreground">
                New alerts appear here within 10 minutes of the condition
                starting.
              </p>
            </>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "rounded-lg border border-border px-3 py-2",
            isLoading && "opacity-60",
          )}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>What</TableHead>
                <TableHead>About</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((item) => {
                const isMuted = item.state === "acknowledged";
                return (
                  <TableRow
                    key={item.id}
                    className={cn(
                      "cursor-pointer",
                      isMuted && "text-muted-foreground",
                    )}
                    onClick={() => setParam("alert", item.id)}
                  >
                    <TableCell className="max-w-md whitespace-normal">
                      <div className="flex items-start">
                        <PriorityDot priority={item.priority} />
                        <div>
                          <button
                            type="button"
                            className={cn(
                              "text-left",
                              !isMuted && "font-semibold",
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setParam("alert", item.id);
                            }}
                          >
                            {item.headline}
                          </button>
                          {isMuted && (
                            <Badge variant="outline" className="ml-2">
                              Acknowledged
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {item.sentence}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <SubjectLink item={item} />
                    </TableCell>
                    <TableCell>{item.customer_name ?? "Stock"}</TableCell>
                    <TableCell>{item.owner_name}</TableCell>
                    <TableCell>
                      <DuePill alert={item} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="mt-2 text-xs text-muted-foreground">
            {list.total === 1 ? "1 alert" : `${list.total} alerts`} · page{" "}
            {list.page} of {Math.max(list.totalPages, 1)} · sorted by due, then
            priority
          </p>
          <ListPagination
            page={list.page}
            totalPages={list.totalPages}
            onPageChange={(next) => setParam("page", String(next))}
          />
        </div>
      )}

      <SlideOverPanel
        open={Boolean(alertId)}
        onOpenChange={(open) => !open && setParam("alert", null)}
        title={openAlert?.headline ?? "Alert"}
        className="w-full sm:max-w-md"
      >
        {alertId && (
          <AlertSheet
            alertId={alertId}
            onChanged={reload}
            onRunSubjectCommand={runSubjectCommand}
          />
        )}
      </SlideOverPanel>
      {shell}
    </div>
  );
}

function SubjectLink({ item }: { item: NeedsAttentionItem }) {
  const href =
    item.subject_kind === "purchase_order" && item.purchase_order_no
      ? paths.purchaseOrderDetail({ purchaseOrderNo: item.purchase_order_no })
      : item.order_no
        ? paths.orderDetail({ orderNo: item.order_no })
        : null;
  if (!href) return <span>{item.subject_ref}</span>;
  return (
    <Link
      className="text-primary underline"
      to={href}
      onClick={(e) => e.stopPropagation()}
    >
      {item.subject_ref}
    </Link>
  );
}
